import { useAuth0 } from '@auth0/auth0-react';
import { useQuery } from '@tanstack/react-query';
import type { Auth0IdTokenClaims } from '@/types/auth';
import type { Region } from '@/constants/regions';

/**
 * Custom hook to fetch and return the user's authorized datacenters from Auth0 ID token claims.
 * Returns undefined if not authenticated or claims are not yet loaded.
 */
export function useUserDatacenters() {
  const { getIdTokenClaims, isAuthenticated, isLoading: auth0Loading } = useAuth0();

  const {
    data: userDatacenters,
    isPending,
    error,
  } = useQuery({
    queryKey: ['user-datacenters'],
    queryFn: async () => {
      try {
        const claims = await getIdTokenClaims();
        // TQ v5 requires queryFn to return a non-undefined value; use null as the
        // "no datacenters" sentinel so the query resolves without error.
        return ((claims as Auth0IdTokenClaims)?.datacenters as Region[] | undefined) ?? null;
      } catch (err) {
        throw err instanceof Error ? err : new Error('Failed to fetch datacenters');
      }
    },
    enabled: isAuthenticated && !auth0Loading,
  });

  const isLoading = auth0Loading || (isAuthenticated && isPending);

  // Map null sentinel (no datacenters claim) back to undefined for callers.
  return { userDatacenters: userDatacenters ?? undefined, isLoading, error };
}
